import { type Node as ProseNode } from 'prosemirror-model'

import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import { findDocHeadings } from 'common/Prose/components/TOC/useDocHeadings'
import { isEmpty, isEqual, isString } from 'lodash'
import React, { useState } from 'react'
import { useInterval } from 'react-use'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'

import Styles from './TableOfContents.module.scss'
import { safePixel } from 'utils'
import { comparer, computed } from 'mobx'
import { useRootStore } from 'stores/RootStore'
import { CommentStatus } from 'types'

export const TableOfContents = observer(() => {
  const { view: editorView, isSelected, node } = useReactNodeView()
  const [headingNodes, setHeadingNodes] = useState(() => findDocHeadings(editorView.state.doc))

  // TODO: Ponder refactoring this as a portal so that a node with more providers can be rendered from RealtimeProvider
  //       Like what is done for FloatingMenuComponent / NodeHandleManager / ContextualBlockActionsManager
  //       This way we can keep a headings observable array in EditorState and update live
  useInterval(() => {
    const headings = findDocHeadings(editorView.state.doc)
    if (!isEqual(headingNodes, headings)) {
      setHeadingNodes(headings)
    }
  }, 2000)

  return (
    <div
      className={cx(Styles.tableOfContents, {
        [Styles.fallback]: headingNodes.length < 1,
        [Styles.selected]: isSelected,
        [Styles.noBoxColor]: !node.attrs.boxColor,
      })}
      style={
        {
          '--toc-box-color': node.attrs.boxColor,
          '--marker-font-color': node.attrs.fontColor,
          '--marker-font-family': node.attrs.fontFamily,
          '--marker-font-size': safePixel(node.attrs.fontSize),
          '--marker-font-weight': node.attrs.fontWeight,
          '--marker-highlight-color': node.attrs.highlightColor,
        } as React.CSSProperties
      }
    >
      <CommentMarkWhenApplicable>
        <HeadingNodes headingNodes={headingNodes} tocAttributes={node.attrs} />
      </CommentMarkWhenApplicable>
    </div>
  )
})

// Keeping this small and relevant for this use case
const CommentMarkWhenApplicable = observer(({ children }: { children: React.ReactNode }) => {
  const { node, isSelected } = useReactNodeView()
  const rootStore = useRootStore()

  // Our comments
  const comments = computed(
    () =>
      rootStore.docStore?.comments.filter((c) => {
        return c.uuid && node.attrs.uuids.includes(c.uuid)
      }) ?? [],
    { equals: comparer.structural },
  ).get()

  if (isEmpty(comments)) return <>{children}</>

  const commentUuids = comments.map((comment) => comment.uuid).filter(isString)
  // If all of the comments are resolved
  const isCommentResolved = comments.length > 0 && comments.every(({ status }) => status === CommentStatus.Resolved)
  // If the highlighted comment is one of ours
  const isCommentHighlighted = comments.some((c) => rootStore.comments.highlighted?.uuid === c.uuid)

  return (
    <mark
      className={cx({ [Styles.commentHighlighted]: (isCommentHighlighted && !isCommentResolved) || isSelected })}
      data-type="comment"
      data-resolved={isCommentResolved}
      data-uuid={commentUuids.join(' ')}
    >
      {children}
    </mark>
  )
})

const HeadingNodes = ({
  headingNodes,
  tocAttributes: { fontColor, highlightColor, fontFamily, fontWeight, fontSize },
}: {
  headingNodes: ProseNode<any>[]
  tocAttributes: Record<string, string | number | null>
}) => {
  if (headingNodes.length < 1)
    return <p className={Styles.fallbackMessage}>Add headings to create a table of contents.</p>

  const isLinearGradient = fontColor ? /linear-gradient/.test(fontColor + '') : false
  const hasHighlightColor = highlightColor != null

  return (
    <>
      {headingNodes.map((node, index) => (
        <p
          key={index} /* TODO: should be node.attrs.blockId but layers was breaking that */
          style={
            {
              '--body-background-color': null,
              '--body-color': fontColor,
              '--body-font-family': fontFamily,
              '--body-font-size': safePixel(fontSize),
              '--body-font-style': null,
              '--body-font-weight': fontWeight,
              '--body-line-height': null,
              '--indent-padding': safePixel((node.attrs.level - 1) * 24),
              '--body-padding-top': null,
              '--body-padding-bottom': null,
              '--body-text-decoration': null,
              '--body-text-transform': null,
              '--body-text-align': null,
            } as any
          }
          className={cx(Styles[`h${node.attrs.level}`], {
            [Styles.active]: index === 1,
            [Styles.highlighted]: hasHighlightColor,
          })}
        >
          <a
            className={cx(Styles.link, { [Styles.gradientFontColor]: isLinearGradient })}
            href={`#${node.attrs.blockId}`}
          >
            {node.textContent}
          </a>
        </p>
      ))}
    </>
  )
}
